import React, { useState } from 'react';
import axios from 'axios';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import styles from './SystemUpdates.module.css';
import FileDropZone from '../../components/FileDropZone';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

const FRONTEND_SYSTEM_UPDATES_ERROR_CODES = {
  FILE_REQUIRED: ErrorCodes.API_INVALID_PARAMETER,
  FILE_UPLOAD_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
  SCRIPT_EXECUTION_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildSystemUpdatesSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const getErrorMessage = (source, fallbackMessage) => {
  if (typeof source === 'string' && source.trim()) {
    return source.trim();
  }

  if (source && typeof source === 'object') {
    if (typeof source.msg === 'string' && source.msg.trim()) {
      return source.msg.trim();
    }
    if (typeof source.message === 'string' && source.message.trim()) {
      return source.message.trim();
    }
  }

  return fallbackMessage;
};

const SystemUpdates = () => {
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [scriptSuccess, setScriptSuccess] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [scriptMessage, setScriptMessage] = useState('');
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);
  
  // File upload state
  const [updateFile, setUpdateFile] = useState({ value: null, error: '' });

  const setSystemUpdatesSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setSupportErrorMessage(buildSystemUpdatesSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearSystemUpdatesSupportError = React.useCallback((force = false) => {
    if (!force) {
      return;
    }
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const notifySystemUpdatesError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    const formattedError = buildSystemUpdatesSupportError(errorCode, pageInfo, sourceMessage);
    setSupportErrorMessage(formattedError);
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);
  
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!updateFile.value) {
      setUpdateFile({ ...updateFile, error: 'Please select a file to upload' });
      notifySystemUpdatesError(
        FRONTEND_SYSTEM_UPDATES_ERROR_CODES.FILE_REQUIRED,
        'System Updates Page / Upload Validation Failed',
        'Please select a file to upload'
      );
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    setUploadSuccess(false);
    setScriptSuccess(false);
    setScriptMessage('');
    
    try {
      const formData = new FormData();
      formData.append('updateFile', updateFile.value);
      
      const response = await axios.post('/api/upload-system-update',formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status) {
        setUploadSuccess(true);
        setUploadedFilename(response.data.filename || updateFile.value.name);
        setUpdateFile({ value: null, error: '' });
      } else {
        const message = getErrorMessage(response.data, 'Failed to upload file. Please try again.');
        setErrorMessage(message);
        setSystemUpdatesSupportError(
          FRONTEND_SYSTEM_UPDATES_ERROR_CODES.FILE_UPLOAD_FAILED,
          'System Updates Page / Upload Failed',
          message
        );
      }
    } catch (error) {
      const message = getErrorMessage(error.response?.data, getErrorMessage(error, 'Failed to upload file. Please try again.'));
      setErrorMessage(message);
      setSystemUpdatesSupportError(
        FRONTEND_SYSTEM_UPDATES_ERROR_CODES.FILE_UPLOAD_FAILED,
        'System Updates Page / Upload Request Error',
        message
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleRunScript = async () => {
    setLoading(true);
    setScriptMessage('');
    setScriptSuccess(false);
    setErrorMessage('');
    
    try {
      const response = await axios.post('/api/run-update-script', {}, { withCredentials: true });
      
      if (response.data.status) {
        setScriptSuccess(true);
        setScriptMessage(response.data.msg || 'Update script executed successfully');
      } else {
        const message = getErrorMessage(response.data, 'Failed to execute update script. Please try again.');
        setErrorMessage(message);
        setSystemUpdatesSupportError(
          FRONTEND_SYSTEM_UPDATES_ERROR_CODES.SCRIPT_EXECUTION_FAILED,
          'System Updates Page / Script Execution Failed',
          message
        );
      }
    } catch (error) {
      const message = getErrorMessage(error.response?.data, getErrorMessage(error, 'Failed to execute update script. Please try again.'));
      setErrorMessage(message);
      setSystemUpdatesSupportError(
        FRONTEND_SYSTEM_UPDATES_ERROR_CODES.SCRIPT_EXECUTION_FAILED,
        'System Updates Page / Script Execution Request Error',
        message
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = () => {
    setUploadSuccess(false);
    setScriptSuccess(false);
    setUploadedFilename('');
    setErrorMessage('');
    setScriptMessage('');
    setUpdateFile({ value: null, error: '' });
    clearSystemUpdatesSupportError(true);
  };
  
  return (
    <Content headerTitle="System Updates" currentMenu="admin">
      {loading && <Loader />}
      
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>System Updates</h2>
          <p className={styles.subtitle}>Upload a sensor (appliance) release and stage it for the fleet, then roll it out from Watchers → Update All. This updates the sensors; the C2C itself is updated by re-running its installer.</p>
        </div>

        {supportErrorMessage && (
          <div className={styles.supportErrorBanner}>
            <div className={styles.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
              <button
                type='button'
                className={styles.copySupportErrorButton}
                onClick={() => clearSystemUpdatesSupportError(true)}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        
        <div className={styles.updateCard}>
          {/* Step 1: File Upload */}
          <div className={styles.step}>
            <div className={styles.stepHeader}>
              <div className={styles.stepNumber}>1</div>
              <div>
                <h3 className={styles.stepTitle}>Upload Appliance Release</h3>
                <p className={styles.stepDescription}>Select the appliance.tar.gz release for this version</p>
              </div>
            </div>
            
            {!uploadSuccess ? (
              <form onSubmit={handleFileUpload} className={styles.uploadForm}>
                <FileDropZone
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setUpdateFile({ value: e.target.files[0], error: '' });
                      setErrorMessage('');
                    }
                  }}
                  selectedFile={updateFile.value}
                  errorMessage={updateFile.error}
                  label="Drop appliance.tar.gz here or click to browse"
                  helperText="The appliance release (appliance.tar.gz), not the full offline bundle"
                />
                
                <button 
                  type="submit" 
                  className={styles.uploadButton}
                  disabled={loading || !updateFile.value}
                >
                  {loading ? 'Uploading...' : 'Upload File'}
                </button>
              </form>
            ) : (
              <div className={styles.successBox}>
                <div className={styles.successIcon}>
                  <svg className={styles.checkIcon}>
                    <use xlinkHref={`/assets/sprite.svg#icon-check`} />
                  </svg>
                </div>
                <div className={styles.successContent}>
                  <p className={styles.successMessage}>File uploaded successfully!</p>
                  <p className={styles.filename}>Filename: <strong>{uploadedFilename}</strong></p>
                </div>
              </div>
            )}
          </div>
          
          {/* Step 2: Run Update Script */}
          <div className={`${styles.step} ${!uploadSuccess ? styles.stepDisabled : ''}`}>
            <div className={styles.stepHeader}>
              <div className={styles.stepNumber}>2</div>
              <div>
                <h3 className={styles.stepTitle}>Stage for the Fleet</h3>
                <p className={styles.stepDescription}>Validate the release and publish it so sensors can update</p>
              </div>
            </div>
            
            {!scriptSuccess ? (
              <div className={styles.scriptSection}>
                <button 
                  onClick={handleRunScript}
                  className={styles.runButton}
                  disabled={loading || !uploadSuccess}
                >
                  {loading ? 'Staging...' : 'Apply Release'}
                </button>
                {!uploadSuccess && (
                  <p className={styles.disabledNote}>Upload a release first to enable staging</p>
                )}
              </div>
            ) : (
              <div className={styles.successBox}>
                <div className={styles.successIcon}>
                  <svg className={styles.checkIcon}>
                    <use xlinkHref={`/assets/sprite.svg#icon-check`} />
                  </svg>
                </div>
                <div className={styles.successContent}>
                  <p className={styles.successMessage}>{scriptMessage}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Error Display */}
          {errorMessage && (
            <div className={styles.errorBox}>
              <div className={styles.errorIcon}>
                <svg className={styles.alertIcon}>
                  <use xlinkHref={`/assets/sprite.svg#icon-alert`} />
                </svg>
              </div>
              <div className={styles.errorContent}>
                <p className={styles.errorText}>{errorMessage}</p>
              </div>
            </div>
          )}
          
          {/* Reset Button */}
          {(uploadSuccess || scriptSuccess) && (
            <div className={styles.resetSection}>
              <button onClick={handleReset} className={styles.resetButton}>
                Upload Another File
              </button>
            </div>
          )}
        </div>
      </div>
    </Content>
  );
};

export default SystemUpdates;
